import React, { useEffect, useMemo, useRef, useState } from 'react';
import { initElbScope } from '../../../../analytics/trackElbAction';
import { FormattedMessage, useIntl } from '../../../../util/reactIntl';
import Modal from '../Utils/Modal/Modal';
import { ReviewsList } from '../Reviews/ReviewsList';
import { getListingReviewStats } from '../reviewStats';
import css from './ReviewsSection.module.css';

const avatarPlaceholder =
  'https://res.cloudinary.com/dlcacck3a/image/upload/v1769082519/socialbnb/assets/icons/avatarPlaceholder.webp';

const MAX_PREVIEW = 160;

const getReviewText = (review, currentLanguage) => {
  if (!review) return '';
  const { text, text_en, text_de, attributes } = review;
  const originalText = text || attributes?.content || '';
  if (currentLanguage === 'en') return text_en || originalText;
  if (currentLanguage === 'de') return text_de || originalText;
  return originalText;
};

const getAuthorName = review => {
  const fullName = review?.author_name || review?.author?.attributes?.profile?.displayName || '';
  return fullName.split(' ')[0] || fullName;
};

const getAuthorImage = review =>
  review?.profile_photo_url ||
  review?.author?.profileImage?.attributes?.variants?.['square-small']?.url ||
  avatarPlaceholder;

const getFormattedDate = (review, locale) => {
  const timestamp = review?.time || review?.attributes?.createdAt;
  if (!timestamp) return '';
  const date = new Date(review.time ? review.time * 1000 : timestamp);
  const month = date.toLocaleString(locale === 'de' ? 'de-DE' : 'en-US', { month: 'long' });
  return `${month} ${date.getFullYear()}`;
};

const getAuthorMeta = (review, locale) => {
  const date = getFormattedDate(review, locale);
  const country =
    review?.author_country ||
    review?.country ||
    review?.author?.attributes?.profile?.publicData?.country;

  if (country && date) {
    return `${country} · ${date}`;
  }

  return date;
};

const getRatingStars = rating => {
  const rounded = Math.round(rating || 0);
  return '★'.repeat(Math.max(0, Math.min(5, rounded)));
};

const ReviewsSection = ({
  currentListing,
  reviews = [],
  googleReviews = [],
  currentLanguage = 'de',
  showModalFromOutside,
  isModalOpen = false,
  closeModalFromOutside,
  displayBothReviews = true,
}) => {
  const intl = useIntl();
  const [showModal, setShowModal] = useState(false);
  const [expandedReviews, setExpandedReviews] = useState([]);

  const publicData = currentListing?.attributes?.publicData || {};
  const googleReviewsFinal =
    googleReviews?.length > 0
      ? googleReviews
      : publicData?.location?.reviews || publicData?.googleReviews || [];

  const combinedReviews = useMemo(() => {
    if (displayBothReviews) {
      return [...reviews, ...googleReviewsFinal];
    }
    return reviews.length > 0 ? reviews : googleReviewsFinal;
  }, [reviews, googleReviewsFinal, displayBothReviews]);

  const reviewStats = getListingReviewStats(currentListing, reviews);
  const totalCount = reviewStats.count || combinedReviews.length;
  const finalRating = reviewStats.rating;

  useEffect(() => {
    setExpandedReviews(Array(combinedReviews.length).fill(false));
  }, [combinedReviews.length]);

  useEffect(() => {
    if (isModalOpen) {
      setShowModal(true);
    }
  }, [isModalOpen]);

  const featuredReviews = combinedReviews.slice(0, 2);
  const formattedRating = intl.formatNumber(finalRating, {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  });

  const openAllReviews = () => {
    setShowModal(true);
    if (showModalFromOutside) {
      showModalFromOutside(true);
    }
  };

  const closeAllReviews = () => {
    setShowModal(false);
    if (closeModalFromOutside) {
      closeModalFromOutside(false);
    }
  };

  // Reviews arrive asynchronously and the section is hidden until they do, so it
  // mounts after Walker's page scan — register the `visible` trigger by hand.
  const sectionRef = useRef(null);
  useEffect(() => {
    initElbScope(sectionRef.current);
  }, [combinedReviews.length]);

  if (combinedReviews.length === 0 || finalRating <= 0) {
    return null;
  }

  return (
    <section
      id="listing-reviews-section"
      ref={sectionRef}
      className={css.section}
      data-elbaction={`visible:vis reviews-${combinedReviews.length}`}
    >
      <div className={css.header}>
        <h2 className={css.title}>
          <FormattedMessage id="ListingPage.reviews" />
        </h2>
        <span className={css.score}>★ {formattedRating}</span>
        <span className={css.count}>
          ·{' '}
          <FormattedMessage id="ListingHeader.reviewCount" values={{ count: totalCount }} />
        </span>
      </div>

      <div className={css.grid}>
        {featuredReviews.map((review, index) => {
          const text = getReviewText(review, currentLanguage);
          const preview =
            text.length > MAX_PREVIEW ? `${text.slice(0, MAX_PREVIEW).trim()}…` : text;
          const rating = review?.rating || review?.attributes?.rating || finalRating;

          return (
            <article
              key={index}
              className={css.card}
              onClick={openAllReviews}
            >
              <div className={css.stars}>{getRatingStars(rating)}</div>
              <p className={css.quote}>„{preview}"</p>
              <div className={css.authorRow}>
                <img
                  src={getAuthorImage(review)}
                  alt=""
                  className={css.avatar}
                  onError={event => {
                    event.currentTarget.src = avatarPlaceholder;
                  }}
                />
                <div className={css.authorText}>
                  <div className={css.authorName}>{getAuthorName(review)}</div>
                  <div className={css.authorMeta}>
                    {getAuthorMeta(review, currentLanguage)}
                  </div>
                </div>
              </div>
            </article>
          );
        })}
      </div>

      {combinedReviews.length > 1 && (
        <button type="button" className={css.showAllBtn} onClick={openAllReviews}>
          <FormattedMessage id="ReviewsSection.showAll" values={{ count: totalCount }} />
        </button>
      )}

      {showModal && (
        <Modal
          isOpen={showModal}
          onClose={closeAllReviews}
          classNameButton="btnReviews"
          isReviewModal
          classNameModal="fullHeightModal"
        >
          <ReviewsList
            reviews={combinedReviews}
            visibleCount={combinedReviews.length}
            expanded={expandedReviews}
            onExpand={index =>
              setExpandedReviews(prev => prev.map((value, i) => (i === index ? !value : value)))
            }
            isGoogleReview={displayBothReviews ? undefined : reviews.length === 0}
            currentLanguage={currentLanguage}
            listingId={currentListing?.id?.uuid}
          />
        </Modal>
      )}
    </section>
  );
};

export default ReviewsSection;
